import React from 'react'

import { Button } from '@/components/atoms/Button/Button.tsx'
import { Icons } from '@/components/atoms/Icons/Icons.tsx'
import { NavLink } from '@/components/atoms/NavLink/NavLink.tsx'
import { SearchBox } from '@/components/atoms/SearchBox/SearchBox.tsx'
import {
  type Dropdown,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/Select/Select.tsx'
import { SortButton, type SortButtonProps } from '@/components/atoms/SortButton/SortButton.tsx'
import { cn } from '@/lib/utils.ts'

export interface NavigationLink {
  title: string
  value: 'pending' | 'assigned' | 'allocated' | 'onboarded' | 'completed'
}

export interface NavigationLabels {
  heading: string
  button: {
    map: string
    list: string
  }
}

export interface NavigationOptions {
  teamStartingLocationOptions: Dropdown
  teamBaseOptions: Dropdown
  statusSelector: NavigationLink[]
}

export interface NavigationState {
  view: 'list' | 'map'
  selectedStartingLocation?: string
  selectedBase?: string
  activeStatus: NavigationLink['value']
  requestSortBy: SortButtonProps['sortBy']
  completedRequestSortBy: SortButtonProps['sortBy']
  disabled?: boolean
}

export interface NavigationAction {
  onViewChanged: (view: 'map' | 'list') => void
  onStartingLocationChanged: (value: string) => void
  onBaseChanged: (value: string) => void
  onNavigationChanged: (nav: 'pending' | 'assigned' | 'allocated' | 'onboarded' | 'completed') => void
  onSearchChanged: (value: string) => void
  onNavigationSortChanged: (
    nav: 'pending' | 'assigned' | 'allocated' | 'onboarded' | 'completed',
    value: 'asc' | 'desc'
  ) => void
}

export interface NavigationProps extends React.HtmlHTMLAttributes<HTMLDivElement>, NavigationOptions, NavigationAction {
  labels: NavigationLabels
  state: NavigationState
}

export const Navigation = React.forwardRef<HTMLDivElement, NavigationProps>(
  (
    {
      className,
      labels,
      state,
      teamStartingLocationOptions,
      teamBaseOptions,
      statusSelector,
      onViewChanged,
      onStartingLocationChanged,
      onBaseChanged,
      onNavigationChanged,
      onSearchChanged,
      onNavigationSortChanged,
      ...props
    },
    ref
  ) => {
    const selectedBase = state.selectedBase ?? teamBaseOptions.options.find((option) => option.default)?.value
    const selectedStartingLocation =
      state.selectedStartingLocation ?? teamStartingLocationOptions.options.find((option) => option.default)?.value
    return (
      <div
        className={cn(
          'flex w-full flex-col gap-3.5 border-b border-b-gray-400 bg-white py-4 pr-[172px] pl-16',
          state.disabled && 'opacity-10',
          className
        )}
        ref={ref}
        {...props}>
        <div className="flex items-center justify-between">
          <div className="inline-flex items-end gap-6">
            <h2 className="text-xl text-gray-800 select-none">{labels.heading}</h2>

            <div className="flex items-end gap-2 self-stretch">
              <Select
                value={selectedStartingLocation}
                onValueChange={onStartingLocationChanged}
                disabled={state.disabled}>
                <SelectTrigger className="w-fit">
                  <SelectValue placeholder={teamStartingLocationOptions.placeholder}>{`${
                    teamStartingLocationOptions.placeholder
                  } ${
                    teamStartingLocationOptions.options.find((option) => option.value === selectedStartingLocation)
                      ?.text ?? ''
                  }`}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {teamStartingLocationOptions.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.text}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>

              <Select value={selectedBase} onValueChange={onBaseChanged} disabled={state.disabled}>
                <SelectTrigger className="w-fit">
                  <SelectValue placeholder={teamBaseOptions.placeholder}>{`${teamBaseOptions.placeholder} ${
                    teamBaseOptions.options.find((option) => option.value === selectedBase)?.text ?? ''
                  }`}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {teamBaseOptions.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.text}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          {state.view === 'list' ? (
            <Button
              size="sm"
              disabled={state.disabled}
              onClick={() => {
                onViewChanged('map')
              }}>
              <Icons.map className="size-3" />
              <span>{labels.button.map}</span>
            </Button>
          ) : (
            <Button
              size="sm"
              disabled={state.disabled}
              onClick={() => {
                onViewChanged('list')
              }}>
              <Icons.list className="size-3" />
              <span>{labels.button.list}</span>
            </Button>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-start gap-2">
            {statusSelector.map((nav) => (
              <NavLink
                key={nav.title}
                state={state.activeStatus === nav.value ? 'active' : 'default'}
                disabled={state.disabled}
                onClick={() => {
                  onNavigationChanged(nav.value)
                }}>
                {nav.title}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-5">
            <SearchBox disabled={state.disabled} onChange={onSearchChanged} />
            <SortButton
              sortBy={state.activeStatus === 'completed' ? state.completedRequestSortBy : state.requestSortBy}
              disabled={state.disabled}
              onSortChanged={(value) => {
                onNavigationSortChanged(state.activeStatus, value)
              }}
            />
          </div>
        </div>
      </div>
    )
  }
)
